import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Plus, Trash2, Pencil, Receipt as ReceiptIcon, FileText, FileDown,
  X, RefreshCw, TrendingUp, TrendingDown, Calculator, Filter, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from "recharts";
import AIInvoiceUploadModal from "../components/AIInvoiceUploadModal";
import { formatCurrencyTotals, formatMoney, primaryCurrency } from "../lib/money";

const CAT_LABELS = {
  travel: "Travel & flights",
  equipment: "Equipment",
  software: "Software & SaaS",
  marketing: "Marketing & ads",
  studio_rental: "Studio rental",
  talent_payout: "Talent payout",
  supplies: "Supplies",
  subscription: "Subscriptions",
  tax: "Tax",
  legal: "Legal",
  other: "Other",
};
const CAT_COLORS = {
  travel: "#06B6D4", equipment: "#a855f7", software: "#6366f1",
  marketing: "#f97316", studio_rental: "#ec4899", talent_payout: "#22c55e",
  supplies: "#fbbf24", subscription: "#10b981", tax: "#dc2626", legal: "#94a3b8", other: "#64748b",
};

const empty = () => ({
  date: new Date().toISOString().slice(0, 10),
  amount: "", currency: "USD", category: "travel", vendor: "", description: "", notes: "",
  receipt_url: "", receipt_public_id: "",
  linked_tour_id: "", is_billable: false, is_paid: true,
});

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tours, setTours] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [fltCat, setFltCat] = useState("all");
  const [fltMonth, setFltMonth] = useState("all");
  const [fltTour, setFltTour] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [showInvoiceUpload, setShowInvoiceUpload] = useState(false);
  const [chartCurrency, setChartCurrency] = useState(null);

  const load = async () => {
    const [e, s, t] = await Promise.all([
      api.get("/api/expenses"),
      api.get("/api/expenses/summary"),
      api.get("/api/tours/").catch(() => ({ data: [] })),
    ]);
    setRows(e.data); setSummary(s.data); setTours(t.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (fltCat !== "all" && r.category !== fltCat) return false;
    if (fltMonth !== "all" && !String(r.date).startsWith(fltMonth)) return false;
    if (fltTour !== "all" && r.linked_tour_id !== fltTour) return false;
    return true;
  }), [rows, fltCat, fltMonth, fltTour]);
  // Per currency: EUR and USD expenses are never added together.
  const filteredTotal = useMemo(() => filtered.reduce((acc, r) => {
    const c = String(r.currency || "USD").toUpperCase();
    acc[c] = (acc[c] || 0) + Number(r.amount || 0);
    return acc;
  }, {}), [filtered]);
  const months = useMemo(() => Array.from(new Set(rows.map(r => String(r.date).slice(0, 7)))).sort().reverse(), [rows]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty(), ...r, amount: String(r.amount), linked_tour_id: r.linked_tour_id || "" }); setOpen(true); };
  const save = async () => {
    if (!form.amount || !form.date) return toast.error("Amount and date required");
    const payload = { ...form, amount: parseFloat(form.amount), linked_tour_id: form.linked_tour_id || null };
    try {
      if (editing) await api.put(`/api/expenses/${editing.id}`, payload);
      else await api.post("/api/expenses", payload);
      setOpen(false); toast.success(editing ? "Updated" : "Logged"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/api/expenses/${id}`); load(); };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} expenses?`)) return;
    const r = await api.post("/api/expenses/bulk-delete", { ids });
    toast.success(`Deleted ${r.data.deleted}`); setSelected(new Set()); load();
  };

  const handleInvoiceExtracted = (data) => {
    // Pre-fill form with extracted data
    setForm({
      ...empty(),
      vendor: data.vendor || '',
      amount: String(data.amount || ''),
      currency: data.currency || 'USD',
      category: data.category || 'other',
      date: data.date || new Date().toISOString().slice(0, 10),
      description: data.description || '',
    });
    setOpen(true);
    toast.success('Invoice data loaded! Review and save.');
  };

  // Charts show one currency at a time (default: the largest expense currency).
  const chartCurrencies = useMemo(() => Object.keys(summary?.by_category_currency || {}).sort(), [summary]);
  const chartCur = chartCurrencies.includes(chartCurrency) ? chartCurrency : primaryCurrency(summary?.total_expenses_by_currency, chartCurrencies[0] || "USD");
  const catData = useMemo(() => Object.entries(summary?.by_category_currency?.[chartCur] || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ name: CAT_LABELS[k] || k, value: v, key: k })), [summary, chartCur]);
  const monthData = useMemo(() => Object.entries(summary?.by_month_currency?.[chartCur] || {}).map(([m, v]) => ({ month: m, value: v })), [summary, chartCur]);
  const money0 = (v) => formatMoney(v, chartCur, { decimals: 0 });
  const totals = (m) => formatCurrencyTotals(m, { decimals: 0, empty: "—" });
  const marginText = Object.keys(summary?.profit_margin_pct_by_currency || {}).length
    ? Object.entries(summary.profit_margin_pct_by_currency).map(([c, v]) => `${c} ${Number(v).toFixed(1)}%`).join(" · ")
    : "—";

  return (
    <AppLayout title="Expenses & P&L" overline={`${rows.length} ENTRIES · INVOICE TRACKING · NET MARGIN ${marginText}`}
      actions={
        <div className="flex gap-2">
          <button 
            onClick={() => setShowInvoiceUpload(true)} 
            className="btn-secondary inline-flex items-center gap-2">
            <Upload size={16} /> 
            AI Invoice Upload
          </button>
          <button onClick={openNew} data-testid="exp-new-button" className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> Log expense
          </button>
        </div>
      }>

      {/* P&L KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 fade-up" data-testid="pnl-kpis">
        <Kpi label="Studio payouts (all-time)" value={totals(summary?.total_revenue_by_currency)} icon={<TrendingUp size={16} />} testid="kpi-rev" pos />
        <Kpi label="Expenses (all-time)" value={totals(summary?.total_expenses_by_currency)} icon={<TrendingDown size={16} />} testid="kpi-exp" neg />
        <Kpi label="Net (per currency)"  value={formatCurrencyTotals(summary?.net_profit_by_currency, { decimals: 0, empty: "—", keepZero: true })} icon={<Calculator size={16} />} testid="kpi-net" big pos={Object.values(summary?.net_profit_by_currency || {}).every((v) => v >= 0)} />
        <Kpi label="Margin"              value={marginText} icon={<TrendingUp size={16} />} testid="kpi-margin" />
      </section>

      {/* Charts */}
      <div className="grid lg:grid-cols-[2fr_1fr] gap-5 mb-6">
        <div className="bg-zinc-950 border rounded-lg p-5" style={{ borderColor: "var(--gio-border)" }} data-testid="exp-month-chart">
          <div className="overline mb-3 flex items-center justify-between gap-2">
            <span>EXPENSES PER MONTH · ALL-TIME · {chartCur}</span>
            {chartCurrencies.length > 1 && (
              <select value={chartCur} onChange={(e) => setChartCurrency(e.target.value)} data-testid="exp-chart-currency" className="gio-input" style={{ width: "auto" }}>
                {chartCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
          {monthData.length === 0 ? <Empty>No data yet.</Empty> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="month" stroke="#8c8c96" fontSize={11} />
                <YAxis stroke="#8c8c96" fontSize={11} tickFormatter={money0} />
                <Tooltip contentStyle={{ background: "#0d0d10", border: "1px solid #3a3a3e", fontSize: 12 }} formatter={money0} />
                <Area type="monotone" dataKey="value" stroke="#dc2626" fill="#dc2626" fillOpacity={0.35} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-zinc-950 border rounded-lg p-5" style={{ borderColor: "var(--gio-border)" }} data-testid="exp-cat-chart">
          <div className="overline mb-3">BY CATEGORY · {chartCur}</div>
          {catData.length === 0 ? <Empty>No data yet.</Empty> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} stroke="#0A0F1A">
                  {catData.map(d => <Cell key={d.key} fill={CAT_COLORS[d.key] || "#94a3b8"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0d0d10", border: "1px solid #3a3a3e", fontSize: 12 }} formatter={money0} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="exp-filter-bar">
        <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>
          <Filter size={14} /> Filter
        </div>
        <select value={fltCat} onChange={e => setFltCat(e.target.value)} data-testid="exp-flt-cat" className="gio-input" style={{ width: "auto", minWidth: 140 }}>
          <option value="all">All categories</option>
          {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={fltMonth} onChange={e => setFltMonth(e.target.value)} data-testid="exp-flt-month" className="gio-input" style={{ width: "auto", minWidth: 130 }}>
          <option value="all">All months</option>
          {months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={fltTour} onChange={e => setFltTour(e.target.value)} data-testid="exp-flt-tour" className="gio-input" style={{ width: "auto", minWidth: 160 }}>
          <option value="all">All tours</option>
          {tours.map(t => <option key={t.id} value={t.id}>{t.title || t.name}</option>)}
        </select>
        <span className="ml-auto text-[12px]" style={{ color: "var(--gio-muted-2)" }}>
          {filtered.length} matches · <strong style={{ color: "var(--gio-text)" }} data-testid="exp-filtered-total">{totals(filteredTotal)}</strong>
        </span>
        {selected.size > 0 && (
          <button onClick={bulkDelete} data-testid="exp-bulk-delete" className="text-[12px] font-bold uppercase px-3 py-1 rounded" style={{ background: "var(--gio-primary)", color: "#fff" }}>
            <Trash2 size={12} className="inline mr-1" /> Delete ({selected.size})
          </button>
        )}
      </div>

      {/* Table */}
      <section className="bg-zinc-950 border rounded-lg overflow-hidden" style={{ borderColor: "var(--gio-border)" }} data-testid="exp-table">
        <div className="overflow-x-auto">
        <table className="w-full text-[14px] min-w-[800px]">
          <thead style={{ background: "var(--gio-surface-hi)", borderBottom: "1px solid var(--gio-border)" }}>
            <tr className="text-left">
              <th className="w-8 px-2 py-3"><input type="checkbox" data-testid="exp-sel-all"
                checked={filtered.length > 0 && filtered.every(r => selected.has(r.id))}
                onChange={e => e.target.checked ? setSelected(new Set(filtered.map(r => r.id))) : setSelected(new Set())} /></th>
              {["Date","Vendor","Category","Description","Tour","Receipt","Amount",""].map(h =>
                <th key={h} className="px-3 py-3 text-[12px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-muted)" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-12 text-center" style={{ color: "var(--gio-muted-3)" }}>
                {rows.length === 0 ? "No expenses logged yet. Click 'Log expense'." : "No matches."}
              </td></tr>
            )}
            {filtered.map(r => {
              const tour = tours.find(t => t.id === r.linked_tour_id);
              return (
                <tr key={r.id} className="group hover:bg-[var(--gio-surface-hi)]" style={{ borderBottom: "1px solid var(--gio-border)" }} data-testid={`exp-row-${r.id}`}>
                  <td className="px-2 py-3">
                    <input type="checkbox" data-testid={`exp-sel-${r.id}`}
                      checked={selected.has(r.id)}
                      onChange={() => setSelected(prev => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                  </td>
                  <td className="px-3 py-3 font-mono-ui text-[13px] whitespace-nowrap" style={{ color: "var(--gio-muted-2)" }}>{r.date}</td>
                  <td className="px-3 py-3" style={{ color: "var(--gio-text)" }}>{r.vendor || <span style={{color:"var(--gio-muted-3)"}}>—</span>}</td>
                  <td className="px-3 py-3">
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                      style={{ background: `${CAT_COLORS[r.category]}22`, color: CAT_COLORS[r.category] || "#94a3b8" }}>
                      {CAT_LABELS[r.category] || r.category}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-[12px]" style={{ color: "var(--gio-muted-2)" }}>{r.description}</td>
                  <td className="px-3 py-3 text-[12px]" style={{ color: "var(--gio-muted-2)" }}>{tour?.title || tour?.name || ""}</td>
                  <td className="px-3 py-3">
                    {r.receipt_url ? (
                      <a href={r.receipt_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        className="inline-flex items-center justify-center w-8 h-8 rounded border"
                        style={{ borderColor: "var(--gio-border)", color: "var(--gio-primary)" }} data-testid={`exp-receipt-${r.id}`}>
                        <ReceiptIcon size={14} />
                      </a>
                    ) : <span className="text-[11px]" style={{ color: "var(--gio-muted-3)" }}>—</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono-ui font-semibold whitespace-nowrap" style={{ color: "var(--gio-primary)" }}>-{formatMoney(r.amount, r.currency)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => openEdit(r)} data-testid={`exp-edit-${r.id}`}><Pencil size={14} style={{ color: "var(--gio-muted)" }} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`exp-delete-${r.id}`}><Trash2 size={14} style={{ color: "var(--gio-muted)" }} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </section>

      {open && (
        <ExpenseModal
          form={form} setForm={setForm} editing={editing}
          tours={tours} onSave={save} onClose={() => setOpen(false)}
        />
      )}

      <AIInvoiceUploadModal
        show={showInvoiceUpload}
        onClose={() => setShowInvoiceUpload(false)}
        onSuccess={handleInvoiceExtracted}
      />
    </AppLayout>
  );
}

function ExpenseModal({ form, setForm, editing, tours, onSave, onClose }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error("Receipt must be under 50 MB"); return; }
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", "receipts/expenses");
      fd.append("resource_type", isPdf ? "raw" : "auto");
      const r = await api.post("/api/cloudinary/upload", fd);
      setForm(f => ({ ...f, receipt_url: r.data.secure_url, receipt_public_id: r.data.public_id }));
      toast.success("Invoice uploaded");
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message || err}`);
    } finally { setUploading(false); e.target.value = ""; }
  };

  const isPdfUrl = form.receipt_url && /\.pdf($|\?)/i.test(form.receipt_url);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="exp-modal" onClick={onClose}>
      <div className="rounded-lg max-w-xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)", boxShadow: "var(--gio-shadow-lg)" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-[13px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-primary)" }}>{editing ? "EDIT" : "LOG"} · EXPENSE</div>
          <button onClick={onClose} data-testid="exp-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field l="Amount *"><input data-testid="exp-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="gio-input" /></Field>
          <Field l="Currency"><input data-testid="exp-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="gio-input" /></Field>
          <Field l="Date *"><input data-testid="exp-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="gio-input" /></Field>
          <Field l="Category">
            <select data-testid="exp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="gio-input">
              {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field l="Vendor"><input data-testid="exp-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Lufthansa, B&H Photo, etc." className="gio-input" /></Field>
          <Field l="Linked tour (optional)">
            <select data-testid="exp-tour" value={form.linked_tour_id} onChange={(e) => setForm({ ...form, linked_tour_id: e.target.value })} className="gio-input">
              <option value="">—</option>
              {tours.map(t => <option key={t.id} value={t.id}>{t.title || t.name}</option>)}
            </select>
          </Field>
        </div>
        <Field l="Description"><input data-testid="exp-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Flight ATH→BER for tour" className="gio-input" /></Field>
        <Field l="Notes"><textarea data-testid="exp-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="gio-input" /></Field>

        {/* Invoice / receipt upload */}
        <div className="mb-3">
          <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>
            Invoice / receipt {uploading && <span className="ml-2 text-[color:var(--gio-primary)]">uploading…</span>}
          </div>
          {form.receipt_url ? (
            <div className="flex items-center gap-3 p-3 border rounded" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
              {isPdfUrl ? (
                <div className="w-16 h-16 rounded flex items-center justify-center border" style={{ borderColor: "var(--gio-border)", color: "var(--gio-primary)" }}>
                  <FileText size={24} />
                </div>
              ) : (
                <img src={form.receipt_url} alt="" className="w-16 h-16 object-cover rounded border" style={{ borderColor: "var(--gio-border)" }} />
              )}
              <a href={form.receipt_url} target="_blank" rel="noreferrer" className="flex-1 text-[12px] underline" style={{ color: "var(--gio-primary)" }}>Open full</a>
              <button onClick={() => setForm(f => ({ ...f, receipt_url: "", receipt_public_id: "" }))} data-testid="exp-rcpt-remove">
                <Trash2 size={16} style={{ color: "var(--gio-muted)" }} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded cursor-pointer hover:bg-[var(--gio-primary-soft)] transition"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }} data-testid="exp-rcpt-upload">
              <FileDown size={18} />
              <span className="text-[11px] font-bold uppercase mt-1">Upload invoice (image / PDF)</span>
              <input type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
            </label>
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button onClick={onSave} data-testid="exp-save" className="btn-primary flex-1">Save</button>
          <button onClick={onClose} data-testid="exp-cancel" className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ l, children }) {
  return (
    <div className="mb-3">
      <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>{l}</div>
      {children}
    </div>
  );
}
function Empty({ children }) { return <div className="py-12 text-center text-[12px]" style={{ color: "var(--gio-muted-3)" }}>{children}</div>; }
function Kpi({ label, value, icon, testid, big, pos, neg }) {
  const color = pos ? "#22c55e" : neg ? "var(--gio-primary)" : "var(--gio-text)";
  return (
    <div className="bg-zinc-950 border p-5 rounded-lg" data-testid={testid} style={{ borderColor: "var(--gio-border)" }}>
      <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider mb-3" style={{ color: "var(--gio-muted)" }}>
        {icon}{label}
      </div>
      <div className={`font-heading leading-none ${big ? "text-4xl" : "text-3xl"}`} style={{ color }}>{value}</div>
    </div>
  );
}
